// Presentation copy is separate from the existing, user-maintained product translations.
const copy = {
  en: {
    pageTitle: "Resus — Patient Monitor Simulator for Medical Training",
    skip: "Skip to content",
    navigation: "Main navigation",
    language: "Language",
    menu: "Toggle menu",
    eyebrow: "MEDICAL SIMULATION, ON ANDROID",
    hero: "A patient monitor.",
    heroAccent: "Built for practice.",
    description:
      "A realistic multiparameter patient monitor simulator. Configure vital signs and waveforms for hands-on healthcare training.",
    get: "Get it on Google Play",
    getShort: "Get Resus",
    how: "How it works",
    screenshots: "Screenshots",
    monitor: "PATIENT MONITOR",
    simulation: "SIMULATION",
    realInterface: "Actual Resus interface",
    trainingOnly: "Designed for simulation and training.",
    overview: "Why Resus",
    values: [
      [
        "A familiar monitor experience",
        "Vital signs, waveforms and sounds that bring a simulation to life.",
      ],
      [
        "Your scenario. Your settings.",
        "Configure parameters and pathology models to match your training.",
      ],
      [
        "Training that goes with you",
        "Run Resus on Android phones and tablets, wherever you teach.",
      ],
    ],
    featuresEyebrow: "THE MONITOR IS JUST THE BEGINNING",
    featuresTitle: "Realistic signals.\nMeaningful practice.",
    configure: "Configure rhythms and heart rate",
    features: [
      [
        "Shape the scenario",
        "Choose from 12 heart rhythms, 4 SpO₂ curve models and 3 blood pressure models. Adjust EtCO₂ and respiratory rate.",
      ],
      [
        "Practice the interventions",
        "Explore defibrillation, synchronized cardioversion, pacing, CPR and 38 simulated medications.",
      ],
      [
        "Keep control of the session",
        "Set custom alarms, review the event log and reset the scenario for the next exercise.",
      ],
    ],
    details: "Explore the full capabilities",
    workflowEyebrow: "FROM SETUP TO DEBRIEF",
    workflowTitle: "Your next scenario\nstarts here.",
    audience:
      "For healthcare professionals and teams teaching, demonstrating and practicing clinical and emergency scenarios.",
    steps: [
      [
        "Set the scene",
        "Choose waveform models and configure vital signs for your scenario.",
      ],
      [
        "Run the simulation",
        "Practice interventions with monitor waveforms, audible alarms and realistic device sounds.",
      ],
      [
        "Review and repeat",
        "Use the event log to discuss actions. Reset the settings for a new scenario.",
      ],
    ],
    showcaseTitle: "Get to know your monitor.",
    showcaseText:
      "Explore the actual application, from vital signs to the controls behind each scenario.",
    fullSize: "View full size",
    imageNames: [
      "Patient monitor",
      "Heart rhythms",
      "SpO₂ models",
      "Blood pressure",
      "Capnography",
      "Alarm limits",
      "Medications",
      "Pacemaker",
      "Defibrillator",
    ],
    imageLabels: [
      "Resus simulator showing ECG, SpO₂, blood pressure and capnography waveforms with CPR and defibrillator controls",
      "Heart rhythm selection and adjustable heart rate in Resus",
      "SpO₂ waveform model selection and saturation control",
      "Invasive blood pressure waveform models and pressure controls",
      "EtCO₂ model, capnography and respiratory rate controls",
      "Custom alarm limits for heart rate, SpO₂, blood pressure and EtCO₂",
      "Simulated medication selection alongside the patient monitor",
      "Transcutaneous pacemaker simulation with rate and current controls",
      "Defibrillator simulation with energy selection and shock controls",
    ],
    downloadEyebrow: "READY FOR YOUR NEXT TRAINING SESSION?",
    downloadTitle: "Bring the monitor to your training.",
    contactTitle: "Let’s talk simulation.",
    contactText:
      "Questions, suggestions or feedback? Get in touch with the Resus team.",
    formNote: "* Required. Submission opens Google Forms in a new tab.",
    send: "Send message",
    validation: {
      summary: "Please check the highlighted fields before sending.",
      required: "Please fill in this field.",
      tooLong: "Please use no more than {max} characters.",
      name: "Please enter your name using at least two letters.",
      email: "Please enter a complete email address, such as name@example.com.",
      phone:
        "Please enter a phone number with 7–15 digits, including the country code if needed.",
      junk: "This looks like placeholder text or repeated characters. Please check your entry.",
      message:
        "Please add a few words explaining your question, suggestion or issue.",
      text: "Please enter a description, or leave this optional field empty.",
      hint: "Briefly describe your question or feedback. For an issue, tell us what happened.",
    },
    social: "Social media",
    disclaimer: "For simulation and training. Not for real patient monitoring.",
  },
  pt: {
    pageTitle: "Resus — Simulador de Monitor Multiparamétrico para Treinamento",
    skip: "Ir para o conteúdo",
    navigation: "Navegação principal",
    language: "Idioma",
    menu: "Abrir ou fechar menu",
    eyebrow: "SIMULAÇÃO MÉDICA NO ANDROID",
    hero: "Um monitor de paciente.",
    heroAccent: "Feito para praticar.",
    description:
      "Um simulador realista de monitor multiparamétrico. Configure sinais vitais e formas de onda para o treinamento prático em saúde.",
    get: "Disponível no Google Play",
    getShort: "Baixar Resus",
    how: "Como funciona",
    screenshots: "Telas do app",
    monitor: "MONITOR DE PACIENTE",
    simulation: "SIMULAÇÃO",
    realInterface: "Interface real do Resus",
    trainingOnly: "Desenvolvido para simulação e treinamento.",
    overview: "Por que Resus",
    values: [
      [
        "Uma experiência familiar",
        "Sinais vitais, formas de onda e sons que dão vida à simulação.",
      ],
      [
        "Seu cenário. Seus ajustes.",
        "Configure parâmetros e modelos de patologias para seu treinamento.",
      ],
      [
        "Treinamento onde você estiver",
        "Use o Resus em celulares e tablets Android, onde você ensina.",
      ],
    ],
    featuresEyebrow: "O MONITOR É SÓ O COMEÇO",
    featuresTitle: "Sinais realistas.\nPrática com propósito.",
    configure: "Configure ritmos e frequência cardíaca",
    features: [
      [
        "Crie seu cenário",
        "Escolha entre 12 ritmos cardíacos, 4 modelos de curva de SpO₂ e 3 modelos de pressão arterial. Ajuste EtCO₂ e frequência respiratória.",
      ],
      [
        "Pratique as intervenções",
        "Explore desfibrilação, cardioversão sincronizada, marca-passo, RCP e 38 medicamentos simulados.",
      ],
      [
        "Controle a sessão",
        "Defina alarmes, consulte o registro de eventos e reinicie o cenário para o próximo exercício.",
      ],
    ],
    details: "Explore todas as funcionalidades",
    workflowEyebrow: "DA CONFIGURAÇÃO AO DEBRIEFING",
    workflowTitle: "Seu próximo cenário\ncomeça aqui.",
    audience:
      "Para profissionais e equipes de saúde que ensinam, demonstram e praticam cenários clínicos e de emergência.",
    steps: [
      [
        "Prepare o cenário",
        "Escolha modelos de ondas e configure os sinais vitais para seu cenário.",
      ],
      [
        "Execute a simulação",
        "Pratique intervenções com formas de onda, alarmes e sons realistas de equipamentos.",
      ],
      [
        "Revise e repita",
        "Use o registro de eventos para discutir as ações. Reinicie os ajustes para um novo cenário.",
      ],
    ],
    showcaseTitle: "Conheça seu monitor.",
    showcaseText:
      "Explore o aplicativo real, dos sinais vitais aos controles de cada cenário.",
    fullSize: "Ver em tamanho original",
    imageNames: [
      "Monitor de paciente",
      "Ritmos cardíacos",
      "Modelos de SpO₂",
      "Pressão arterial",
      "Capnografia",
      "Limites de alarmes",
      "Medicamentos",
      "Marca-passo",
      "Desfibrilador",
    ],
    imageLabels: [
      "Simulador Resus com curvas de ECG, SpO₂, pressão arterial e capnografia, controles de RCP e desfibrilador",
      "Seleção de ritmos e ajuste da frequência cardíaca no Resus",
      "Seleção de modelos de onda de SpO₂ e controle de saturação",
      "Modelos de onda e controles da pressão arterial invasiva",
      "Controles do modelo de EtCO₂, capnografia e frequência respiratória",
      "Limites de alarmes para frequência cardíaca, SpO₂, pressão arterial e EtCO₂",
      "Seleção de medicamentos simulados junto ao monitor",
      "Simulação de marca-passo transcutâneo com ajustes de frequência e corrente",
      "Simulação de desfibrilador com seleção de energia e controles de choque",
    ],
    downloadEyebrow: "PRONTO PARA O PRÓXIMO TREINAMENTO?",
    downloadTitle: "Leve o monitor para seu treinamento.",
    contactTitle: "Vamos falar de simulação.",
    contactText: "Dúvidas, sugestões ou comentários? Fale com a equipe Resus.",
    formNote: "* Obrigatório. O envio abre o Google Forms em uma nova aba.",
    send: "Enviar mensagem",
    validation: {
      summary: "Confira os campos destacados antes de enviar.",
      required: "Preencha este campo.",
      tooLong: "Use no máximo {max} caracteres.",
      name: "Digite seu nome usando pelo menos duas letras.",
      email: "Digite um e-mail completo, como nome@exemplo.com.",
      phone:
        "Digite um telefone com 7–15 dígitos, incluindo o código do país se necessário.",
      junk: "Este texto parece ser de teste ou ter caracteres repetidos. Confira o que digitou.",
      message:
        "Escreva algumas palavras explicando sua dúvida, sugestão ou problema.",
      text: "Digite uma descrição ou deixe este campo opcional vazio.",
      hint: "Descreva brevemente sua dúvida ou comentário. Se houver um problema, conte o que aconteceu.",
    },
    social: "Redes sociais",
    disclaimer:
      "Para simulação e treinamento. Não destinado ao monitoramento de pacientes reais.",
  },
  es: {
    pageTitle: "Resus — Simulador de Monitor Multiparamétrico para Formación",
    skip: "Ir al contenido",
    navigation: "Navegación principal",
    language: "Idioma",
    menu: "Abrir o cerrar menú",
    eyebrow: "SIMULACIÓN MÉDICA EN ANDROID",
    hero: "Un monitor de paciente.",
    heroAccent: "Hecho para practicar.",
    description:
      "Un simulador realista de monitor multiparamétrico. Configure signos vitales y formas de onda para la formación práctica en salud.",
    get: "Consíguelo en Google Play",
    getShort: "Obtener Resus",
    how: "Cómo funciona",
    screenshots: "Pantallas",
    monitor: "MONITOR DE PACIENTE",
    simulation: "SIMULACIÓN",
    realInterface: "Interfaz real de Resus",
    trainingOnly: "Diseñado para simulación y formación.",
    overview: "Por qué Resus",
    values: [
      [
        "Una experiencia familiar",
        "Signos vitales, formas de onda y sonidos que dan vida a la simulación.",
      ],
      [
        "Su escenario. Sus ajustes.",
        "Configure parámetros y modelos de patologías para su formación.",
      ],
      [
        "Formación donde esté",
        "Use Resus en teléfonos y tabletas Android, donde usted enseña.",
      ],
    ],
    featuresEyebrow: "EL MONITOR ES SOLO EL COMIENZO",
    featuresTitle: "Señales realistas.\nPráctica con propósito.",
    configure: "Configure ritmos y frecuencia cardíaca",
    features: [
      [
        "Cree su escenario",
        "Elija entre 12 ritmos cardíacos, 4 modelos de curva de SpO₂ y 3 modelos de presión arterial. Ajuste EtCO₂ y frecuencia respiratoria.",
      ],
      [
        "Practique las intervenciones",
        "Explore desfibrilación, cardioversión sincronizada, marcapasos, RCP y 38 medicamentos simulados.",
      ],
      [
        "Controle la sesión",
        "Configure alarmas, consulte el registro de eventos y reinicie el escenario para el siguiente ejercicio.",
      ],
    ],
    details: "Explore todas las funciones",
    workflowEyebrow: "DE LA CONFIGURACIÓN AL DEBRIEFING",
    workflowTitle: "Su próximo escenario\ncomienza aquí.",
    audience:
      "Para profesionales y equipos sanitarios que enseñan, demuestran y practican escenarios clínicos y de emergencia.",
    steps: [
      [
        "Prepare el escenario",
        "Elija modelos de ondas y configure los signos vitales para su escenario.",
      ],
      [
        "Ejecute la simulación",
        "Practique intervenciones con formas de onda, alarmas y sonidos realistas de equipos.",
      ],
      [
        "Revise y repita",
        "Use el registro de eventos para analizar las acciones. Reinicie los ajustes para un nuevo escenario.",
      ],
    ],
    showcaseTitle: "Conozca su monitor.",
    showcaseText:
      "Explore la aplicación real, desde los signos vitales hasta los controles de cada escenario.",
    fullSize: "Ver a tamaño completo",
    imageNames: [
      "Monitor de paciente",
      "Ritmos cardíacos",
      "Modelos de SpO₂",
      "Presión arterial",
      "Capnografía",
      "Límites de alarmas",
      "Medicamentos",
      "Marcapasos",
      "Desfibrilador",
    ],
    imageLabels: [
      "Simulador Resus con ondas de ECG, SpO₂, presión arterial y capnografía, controles de RCP y desfibrilador",
      "Selección de ritmos y ajuste de frecuencia cardíaca en Resus",
      "Selección de modelos de onda de SpO₂ y control de saturación",
      "Modelos de onda y controles de presión arterial invasiva",
      "Controles de EtCO₂, capnografía y frecuencia respiratoria",
      "Límites de alarmas para frecuencia cardíaca, SpO₂, presión arterial y EtCO₂",
      "Selección de medicamentos simulados junto al monitor",
      "Simulación de marcapasos transcutáneo con ajustes de frecuencia y corriente",
      "Simulación de desfibrilador con selección de energía y controles de descarga",
    ],
    downloadEyebrow: "¿LISTO PARA SU PRÓXIMA SESIÓN?",
    downloadTitle: "Lleve el monitor a su formación.",
    contactTitle: "Hablemos de simulación.",
    contactText:
      "¿Preguntas, sugerencias o comentarios? Contacte con el equipo Resus.",
    formNote: "* Obligatorio. El envío abre Google Forms en una pestaña nueva.",
    send: "Enviar mensaje",
    validation: {
      summary: "Revise los campos destacados antes de enviar.",
      required: "Complete este campo.",
      tooLong: "Use como máximo {max} caracteres.",
      name: "Escriba su nombre con al menos dos letras.",
      email: "Escriba un correo completo, como nombre@ejemplo.com.",
      phone:
        "Escriba un teléfono con 7–15 dígitos, incluyendo el código del país si es necesario.",
      junk: "Este texto parece ser de prueba o tener caracteres repetidos. Revise lo que escribió.",
      message:
        "Escriba unas palabras explicando su pregunta, sugerencia o problema.",
      text: "Escriba una descripción o deje este campo opcional vacío.",
      hint: "Describa brevemente su pregunta o comentario. Si hay un problema, cuente lo que ocurrió.",
    },
    social: "Redes sociales",
    disclaimer:
      "Para simulación y formación. No destinado al monitoreo de pacientes reales.",
  },
};
export default copy;
